import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from "@mui/material";
import { useContext, useEffect, useMemo, useState } from "react";
import { axiosInstance } from "../../../utils/networking";
import { StyledButtonMain } from "../../common/Buttons";
import ThriveModal from "../../common/ThriveModal";
import WebIntakeDetail from "./WebIntakeDetail";
import dayjs from "dayjs";
import { UserContext } from "../../../context/UserContext";

type FormSubmission = {
  id?: string | number;
  "Company Name"?: string;
  Name?: string;
  "Business Phone Number"?: string;
  Email?: string;
  "Address (where your business is registered)"?: string;
  City?: string;
  "Zip Code"?: string;
  "Entry Date"?: string;
};

type SortOrder = "asc" | "desc";

type SortState = {
  orderBy: keyof FormSubmission | null;
  order: SortOrder;
};

const FORM_TABLES = [
  { key: "general", label: "General" },
  { key: "global", label: "Global Resource Program" },
  { key: "hospitality", label: "Hospitality" },
];

const COLUMNS: { key: keyof FormSubmission; label: string }[] = [
  { key: "Company Name", label: "Firm name" },
  { key: "Name", label: "Contact name" },
  { key: "Business Phone Number", label: "Phone" },
  { key: "Email", label: "Email" },
  { key: "Address (where your business is registered)", label: "Firm address" },
  { key: "City", label: "City" },
  { key: "Zip Code", label: "Zipcode" },
  { key: "Entry Date", label: "Date" },
];

const MOCK_FORM_DATA: Record<string, FormSubmission[]> = {
  general: [],
  global: [],
  hospitality: [],
};

const WebIntakeForms = () => {
  const [formData, setFormData] =
    useState<Record<string, FormSubmission[]>>(MOCK_FORM_DATA);
  const [sortState, setSortState] = useState<Record<string, SortState>>(() =>
    FORM_TABLES.reduce(
      (acc, table) => {
        acc[table.key] = { orderBy: null, order: "asc" };
        return acc;
      },
      {} as Record<string, SortState>,
    ),
  );
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [intakeDetailOpen, setIntakeDetailOpen] = useState(false);
  const [selectedWebIntake, setSelectedWebIntake] =
    useState<FormSubmission | null>(null);
  const { user } = useContext(UserContext);

  useEffect(() => {
    let isMounted = true;

    const fetchForms = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await axiosInstance.get("/webintake");

        const newFormData: Record<string, FormSubmission[]> = {};
        newFormData["general"] = response.data.webintakes.filter(
          (item: any) => item.source === "general",
        );
        newFormData["global"] = response.data.webintakes.filter(
          (item: any) => item.source === "global",
        );
        newFormData["hospitality"] = response.data.webintakes.filter(
          (item: any) => item.source === "hospitality",
        );
        setFormData(newFormData);
        if (!isMounted) {
          return;
        }
      } catch (err: any) {
        if (!isMounted) {
          return;
        }
        console.error("Unable to load web intake forms", err);
        const message =
          err?.response?.data?.message ??
          err?.message ??
          "Unable to load web intake forms.";
        setError(message);
        setFormData(MOCK_FORM_DATA);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    if (intakeDetailOpen === false) {
      fetchForms();
    }

    return () => {
      isMounted = false;
    };
  }, [intakeDetailOpen]);

  const sortedData = useMemo(() => {
    return FORM_TABLES.reduce(
      (acc, table) => {
        const data = formData[table.key] || [];
        const sort = sortState[table.key];

        if (!sort?.orderBy) {
          acc[table.key] = data;
          return acc;
        }

        const normalizedData = [...data].sort((a, b) => {
          const aValue = (a[sort.orderBy!] ?? "").toString().toLowerCase();
          const bValue = (b[sort.orderBy!] ?? "").toString().toLowerCase();

          if (aValue < bValue) {
            return sort.order === "asc" ? -1 : 1;
          }
          if (aValue > bValue) {
            return sort.order === "asc" ? 1 : -1;
          }
          return 0;
        });

        acc[table.key] = normalizedData;

        return acc;
      },
      {} as Record<string, FormSubmission[]>,
    );
  }, [formData, sortState]);

  const handleSort = (formKey: string, columnKey: keyof FormSubmission) => {
    setSortState((prev) => {
      const current = prev[formKey];
      const isSameColumn = current?.orderBy === columnKey;
      const nextOrder: SortOrder =
        isSameColumn && current?.order === "asc" ? "desc" : "asc";

      return {
        ...prev,
        [formKey]: {
          orderBy: columnKey,
          order: isSameColumn ? nextOrder : "asc",
        },
      };
    });
  };

  const renderTable = (formKey: string, title: string) => {
    const rows = sortedData[formKey] || [];
    const { orderBy, order } = sortState[formKey] || {
      orderBy: null,
      order: "asc" as SortOrder,
    };

    return (
      <Paper elevation={1} key={formKey}>
        <Box p={2}>
          <Typography variant="h6" gutterBottom>
            {title}
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {COLUMNS.map((column) => (
                    <TableCell key={`${formKey}-${column.key}`}>
                      <TableSortLabel
                        active={orderBy === column.key}
                        direction={orderBy === column.key ? order : "asc"}
                        onClick={() => handleSort(formKey, column.key)}
                      >
                        {column.label}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 && (
                  <TableRow hover sx={{ "&:hover": { cursor: "pointer" } }}>
                    <TableCell colSpan={COLUMNS.length + 1}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        align="center"
                      >
                        No submissions available.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                {rows.map((submission, index) => (
                  <TableRow
                    hover
                    key={submission.id ?? `${formKey}-${index}`}
                    sx={{ "&:hover": { cursor: "pointer" } }}
                    onClick={() => {
                      setSelectedWebIntake(submission);
                      setIntakeDetailOpen(true);
                    }}
                  >
                    <TableCell>{submission["Company Name"] || "-"}</TableCell>
                    <TableCell>{submission["Name"] || "-"}</TableCell>
                    <TableCell>
                      {submission["Business Phone Number"] || "-"}
                    </TableCell>
                    <TableCell>{submission["Email"] || "-"}</TableCell>
                    <TableCell>
                      {submission[
                        "Address (where your business is registered)"
                      ] || "-"}
                    </TableCell>
                    <TableCell>{submission.City || "-"}</TableCell>
                    <TableCell>{submission["Zip Code"] || "-"}</TableCell>
                    <TableCell>
                      {submission["Entry Date"]
                        ? dayjs(submission["Entry Date"], "DD/MM/YYYY").format(
                            "MM/DD/YYYY",
                          )
                        : "-"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Paper>
    );
  };

  return [
    "desanctisj@oaklandthrive.org",
    "garcias@oaklandthrive.org",
    "cookt@oaklandthrive.org",
  ].includes(user.username) ? (
    <Box>
      {error && (
        <Box mb={2}>
          <Alert severity="error">{error}</Alert>
        </Box>
      )}
      {loading ? (
        <Stack
          alignItems="center"
          justifyContent="center"
          sx={{ minHeight: 200 }}
        >
          <CircularProgress />
        </Stack>
      ) : (
        <Stack spacing={3}>
          {FORM_TABLES.map((table) => renderTable(table.key, table.label))}
        </Stack>
      )}
      <ThriveModal
        open={intakeDetailOpen}
        setOpen={setIntakeDetailOpen}
        closeByClick={true}
        height={700}
        contentComponent={
          <WebIntakeDetail
            setOpen={setIntakeDetailOpen}
            submission={selectedWebIntake}
            actionMode={true}
          ></WebIntakeDetail>
        }
      ></ThriveModal>
    </Box>
  ) : (
    <Typography variant="h6">Contact Admin for Access to this page</Typography>
  );
};

export default WebIntakeForms;
